import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { DialogTitle, DialogContent, DialogActions, Icon } from "@material-ui/core";
import { WorkspacePlanTier } from "common/interfaces";
import { getWorkspacePlanTierFromProductId } from "common/stripeProducts";
import { Button } from "js/Components/Button";
import { defaultText } from "js/textStyles";
import { themeColors } from "js/react/sharedStyles";
import { getStaticUrl } from "js/config";
import { BeautifulDialog } from "js/react/components/Dialogs/BaseDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Gap10 } from "js/react/components/Gap";

const StyledDialogTitle = styled(DialogTitle)`
    &.MuiDialogTitle-root {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;
        padding:0;
    }

    h2 {
        color: #333;
        text-align: center;
        font-family: "Source Sans Pro";
        font-size: 20px;
        font-style: normal;
        font-weight: 600;
        line-height: 120%;

        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;

        p {
            color: #666;
            font-size: 14px;
            font-weight: 400;
            line-height: 125%; 
        }
    }
`;

const StyledDialogContent = styled(DialogContent)`
    &&& {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 30px;
        padding: 0px;
        ${defaultText}
    }
`;

const StyledDialogActions = styled(DialogActions)`
    &.MuiDialogActions-root {
        padding: 0;
        justify-content: space-between;
        margin: 0px;
        width: 100%;
    }
`;

const RoleText = styled.div`
    color: #333;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 110%;
`;

const CheckCircleHighlightIcon = styled(Icon).attrs({ fontSize: "small" })`
    color: #ffaa00;
    align-self: flex-start;
`;

const DialogBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    padding: 40px 50px;
`;

interface DowngradeDialogProps {
    title: string;
    subtitle: string;
    submitButtonLabel: string;
    /** Stripe Product id from the canceled subscription (matches `productId` on a tier in `stripeProducts`). */
    product?: string | null;
    onSubmit?: (featuredPlan: WorkspacePlanTier) => void;
    closeDialog: () => void;
}

/** Tier to emphasize in upgrade UI: Team path if they were on Team/Enterprise, otherwise Pro. */
function getFeaturedPlanFromStripeProductId(productId: string | null | undefined): WorkspacePlanTier {
    const tier = getWorkspacePlanTierFromProductId(productId);
    if (tier === WorkspacePlanTier.TEAM || tier === WorkspacePlanTier.ENTERPRISE) {
        return WorkspacePlanTier.TEAM;
    }
    return WorkspacePlanTier.PRO;
}

const DowngradeDialog = (props: DowngradeDialogProps) => {
    const { title, subtitle, submitButtonLabel, product, onSubmit, closeDialog } = props;
    const { t } = useTranslation();

    const featuredPlan = getFeaturedPlanFromStripeProductId(product);

    const getTitle = () => {
        return (
            <StyledDialogTitle>
                <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-upgrade-logo.svg")}/>
                <Gap10/>
                <div data-test-id="cancel-or-downgrade-plan-dialog-title">{title}</div>
                <p data-test-id="cancel-or-downgrade-plan-dialog-body">{subtitle}</p>
            </StyledDialogTitle>
        );
    };

    const getWorkspaceUsersBox = () => {
        return (
            <FlexBox left center padding="15px" gap={20}
                style={{ background: "#F7FAFC", border: "1px solid #DAE9F0" }}>
                <FlexBox left column gap={15}>
                    <FlexBox gap={8}>
                        <CheckCircleHighlightIcon>check_circle</CheckCircleHighlightIcon>
                        <RoleText>{t("Create presentations in one click with AI")}</RoleText>
                    </FlexBox>
                    <FlexBox gap={8}>
                        <CheckCircleHighlightIcon>check_circle</CheckCircleHighlightIcon>
                        <RoleText>{t("Hundreds of Smart Slide templates")}</RoleText>
                    </FlexBox>
                </FlexBox>
                <FlexBox left column gap={15}>
                    <FlexBox gap={8}>
                        <CheckCircleHighlightIcon>check_circle</CheckCircleHighlightIcon>
                        <RoleText>{t("Create your own theme")}</RoleText>
                    </FlexBox>
                    <FlexBox gap={8}>
                        <CheckCircleHighlightIcon>check_circle</CheckCircleHighlightIcon>
                        <RoleText>{t("Be a presentation rockstar, make beautiful slides in minutes")}</RoleText>
                    </FlexBox>
                </FlexBox>
            </FlexBox>
        );
    };

    const handleConfirm = () => {
        onSubmit?.(featuredPlan);
    };

    return (
        <BeautifulDialog preventClose closeDialog={closeDialog}>
            <DialogBody>
                {getTitle()}
                <StyledDialogContent>
                    {getWorkspaceUsersBox()}
                </StyledDialogContent>
                <StyledDialogActions>
                    <Button onClick={closeDialog} unframed large color={themeColors.mediumGray}>
                        Not now
                    </Button>
                    <Button upgrade large onClick={handleConfirm}>
                        {submitButtonLabel}
                    </Button>
                </StyledDialogActions>
            </DialogBody>
        </BeautifulDialog>
    );
};

export default DowngradeDialog;
